import { DOCUMENT, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, catchError, distinctUntilChanged, filter, of, switchMap, tap } from 'rxjs';

import { environment } from '@www-src/environments/environment';
import { ContentArea } from './content.models';

// The app's address for each layer of areas.json. A route in areas.json names the production
// app; its path is kept and put on this environment's app, so dev links open the dev apps.
const LAYER_BASES: Record<string, string> = {
  account: environment.ACCOUNT_CLIENT_URL,
  admin: environment.MANAGE_CLIENT_URL,
  case: environment.APP_CLIENT_URL,
  client: environment.APP_CLIENT_URL,
};

// A hub subdomain: letters, digits and dashes only, so it can go into an address safely.
const HUB_PATTERN = /^[a-z0-9-]{1,63}$/;

// How long the CDN may cache a hub's info.json, in seconds.
const HUB_INFO_MAX_AGE = 3600;

interface HubInfo {
  name?: unknown;
}

// The reader's hub and case, read from ?hub= and &case= when the app opens the help center,
// and the hub's firm name from the public CDN. Kept in memory for this visit only, never stored.
// Prerendered pages have none, so app links show as plain text and the firm reads
// "your law firm" until the browser reads the address.
@Injectable({
  providedIn: 'root'
})
export class ReaderHubService {
  public readonly hub = signal<string | null>(null);
  public readonly caseId = signal<string | null>(null);
  public readonly firmName = signal<string | null>(null);

  private router = inject(Router);
  private http = inject(HttpClient);
  private hubChanges = new Subject<string>();

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) {
      return;
    }

    // A new hub drops the last hub's name and any reply still on its way. An unknown hub, a hub
    // being deleted or a failed request leaves no name.
    this.hubChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => this.firmName.set(null)),
        switchMap(hub => this.http
          .get<HubInfo>(`${environment.PUBLIC_CDN_URL}/hosts/${hub}/info/info.json`, { params: { max_age: HUB_INFO_MAX_AGE } })
          .pipe(catchError(() => of(null))))
      )
      .subscribe(info => {
        const name = typeof info?.name === 'string' ? info.name.trim() : '';

        this.firmName.set(name || null);
      });

    // The service starts with the first help page, which may be after the first navigation ended.
    this.read(new URLSearchParams(inject(DOCUMENT).defaultView.location.search));

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const params = this.router.parseUrl(event.urlAfterRedirects).queryParams;

        this.read(new URLSearchParams(params));
      });
  }

  // Links inside the site don't carry the hub, so only an address that names one changes it.
  private read(params: URLSearchParams): void {
    const hub = (params.get('hub') ?? '').toLowerCase();

    if (HUB_PATTERN.test(hub)) {
      this.hub.set(hub);
      this.caseId.set(params.get('case') || null);
      this.hubChanges.next(hub);
    }
  }

  // ?hub= and &case= for the links that keep them (the client help pages and the switch between
  // the two help homes), or null when the reader came without a hub.
  public linkParams(): Record<string, string> | null {
    const hub = this.hub();
    const caseId = this.caseId();

    if (!hub) {
      return null;
    }

    return caseId ? { hub, case: caseId } : { hub };
  }

  // The address of an area in the reader's hub, or null when it can't be built: no hub, an area
  // without a route, or a case area while the reader isn't on a case.
  public addressOf(area: ContentArea | undefined): string | null {
    const hub = this.hub();

    if (!hub || !area?.route) {
      return null;
    }

    const route = new URL(area.route.replace(':hub', 'hub'));
    let path = `${route.pathname}${route.search}${route.hash}`;

    if (path.includes(':caseId')) {
      const caseId = this.caseId();

      if (!caseId) {
        return null;
      }

      path = path.replace(':caseId', encodeURIComponent(caseId));
    }

    const base = LAYER_BASES[area.layer] ?? route.origin;

    return `${base.replace('*', hub).replace(/\/$/, '')}${path}`;
  }
}
